<template>
  <rest-form-page
    :page-title="pageTitle"
    :fetch-endpoint="fetchEndpoint"
    :save-endpoint="{}"
    :model="formModel"
    :nosave="true"
    :readonly="false"
  >
    <template slot="form" v-if="formModel">
      <el-dialog
        v-if="formModel"
        :title="dialogTitle"
        :visible="!!showMode"
        :close-on-click-modal="false"
        :before-close="closeKeyDialog"
        :modal="false"
        :modal-append-to-body="false"
      >
        <el-button
          type="text"
          class="copy-key-button"
          icon="el-icon-copy-document"
          @click="copyKey"
        ></el-button>
        <el-input
          :readonly="true"
          :disabled="true"
          type="text"
          class="popup-textarea"
          v-model="showData"
          v-if="showMode === 'fingerprint'"
        />
        <el-input
          :readonly="true"
          :disabled="true"
          type="textarea"
          class="popup-textarea"
          v-model="showData"
          v-if="showMode === 'publicKey'"
        />
      </el-dialog>
      <el-dialog
        v-if="updateKeyRow"
        :title="$t('ApiIntegration_PopupPk_Title')"
        :visible="!!updateKeyRow"
        :close-on-click-modal="false"
        :before-close="closeKeyDialog"
        :modal="false"
        :modal-append-to-body="false"
      >
        <el-input
          type="textarea"
          spellcheck="false"
          class="popup-textarea"
          v-model="addPublicKey"
        />
        <span slot="footer" class="dialog-footer">
          <el-button type="primary" plain @click="closeKeyDialog">
            <span>{{ $t('Button_Cancel') }}</span>
          </el-button>
          <el-button
            type="primary"
            @click="updatePublicKey"
            :disabled="!addPublicKey"
          >
            <span>{{ $t('ApplicationList_Button_Add') }}</span>
            <i class="el-icon-arrow-right el-icon--save" />
          </el-button>
        </span>
      </el-dialog>
      <el-dialog
        :title="$t('ApiIntegration_GeneratedSecret_Title')"
        :visible="!!generatedSecret"
        :close-on-click-modal="false"
        :before-close="closeSecretDialog"
        :modal="false"
        :modal-append-to-body="false"
        width="620px"
      >
        <el-alert
          :title="$t('ApiIntegration_GeneratedSecret_Warning')"
          type="warning"
          :closable="false"
          show-icon
          style="margin-bottom: 16px"
        />
        <el-input :readonly="true" type="text" v-model="generatedSecret" />
        <el-button
          type="text"
          class="copy-secret-button"
          icon="el-icon-copy-document"
          @click="copySecret"
        ></el-button>
        <span slot="footer" class="dialog-footer">
          <el-button type="primary" @click="closeSecretDialog">
            <span>{{ $t('Button_Close') }}</span>
          </el-button>
        </span>
      </el-dialog>
      <form-section
        :title="$t('ApiIntegration_Section_Title')"
        v-if="$can('manageProjectTechnical')"
      >
        <template slot="controls">
          <el-button
            slot="actions"
            type="primary"
            icon="el-icon-circle-plus"
            @click="addRow"
          >
            {{ $t('ApiIntegration_Button_AddPublicKey') }}
          </el-button>
        </template>

        <el-row>
          <el-col>
            <rest-list-editable
              :payload="fetchTokenEndpoint"
              :add-endpoint="addTokenEndpoint"
              :rules="rules"
              :before-save="beforeSaveRow"
              ref="rest-list"
              @dataLoaded="dataLoaded"
              @row-updated="onRowUpdated"
              :click-outside-to-cancel="false"
            >
              <rest-list-column
                prop="name"
                :title="$t('ApiIntegration_Column_Name')"
                :editable="true"
                :width="175"
              >
                <template slot="input-edit" slot-scope="{ model }">
                  <el-input
                    :placeholder="$t('ApiIntegration_Column_Name')"
                    v-model="model.name"
                  />
                </template>
              </rest-list-column>
              <rest-list-column
                prop="client_id"
                :title="$t('ApiIntegration_Column_ClientID')"
                :editable="false"
              >
              </rest-list-column>
              <rest-list-column-enum
                prop="type"
                :title="$t('ApiIntegration_Type_Column_Title')"
                :filterable="true"
                :enum="oauthTypeEnum"
                :sortable="false"
                :editable="true"
                :width="125"
              >
                <template slot-scope="scope">
                  {{
                    oauthTypeEnum[scope.row.type]
                      ? $t(oauthTypeEnum[scope.row.type])
                      : ''
                  }}
                </template>
                <template slot="input-edit" slot-scope="{ model }">
                  <el-select v-model="model.type" :disabled="!model.newRow">
                    <el-option
                      :value="2"
                      :label="$t('ApiIntegration_Type_Secret')"
                    />
                    <el-option
                      :value="1"
                      :label="$t('ApiIntegration_Type_PublicKey')"
                    />
                  </el-select>
                </template>
              </rest-list-column-enum>
              <rest-list-column
                prop="public_key"
                :title="$t('ApiIntegration_Column_PublicKeyOrFingerprint')"
                :filterable="false"
                :filter-input="false"
                :sortable="false"
                :editable="true"
                :width="180"
              >
                <template slot-scope="scope">
                  <span v-if="scope.row.type === 1">
                    <el-button
                      v-if="scope.row.public_key"
                      size="mini"
                      @click="showKey(scope)"
                      >{{ $t('ApiIntegration_ShowBtn') }}
                      <el-icon name="view" />
                    </el-button>
                    <el-button v-else size="mini" @click="addKey(scope)"
                      >{{ $t('ApiIntegration_AddBtn') }}
                      <el-icon name="circle-plus" />
                    </el-button>
                  </span>
                  <span v-if="scope.row.type === 3">
                    <el-button
                      v-if="scope.row.public_key"
                      size="mini"
                      @click="showFp(scope)"
                      >{{ $t('ApiIntegration_ShowFpBtn') }}
                      <el-icon name="view" />
                    </el-button>
                  </span>
                </template>
                <template slot="input-edit">
                  <div></div>
                </template>
              </rest-list-column>
              <rest-list-column
                prop="scopes"
                :title="$t('ApiIntegration_Scopes_Column_Title')"
                :filterable="false"
                :filter-input="false"
                :sortable="false"
                :editable="true"
                :width="150"
              >
                <template slot-scope="scope">
                  <el-button size="mini" @click="setScopes(scope)"
                    >{{ $t('ApiIntegration_Scopes_SetScopesBtn') }}
                    <el-icon name="setting" />
                  </el-button>
                </template>
                <template slot="input-edit">
                  <div></div>
                </template>
              </rest-list-column>
              <rest-list-column
                prop="expires_at"
                :title="$t('ApiIntegration_ExpiresAt_Column_Title')"
                :filterable="false"
                :sortable="true"
                :editable="true"
                :width="180"
              >
                <template slot-scope="scope">
                  <span v-if="scope.row.expires_at">
                    {{ formatDate(scope.row.expires_at) }}
                  </span>
                  <span v-else class="text-muted">
                    {{ $t('ApiIntegration_ExpiresAt_Never') }}
                  </span>
                </template>
                <template slot="input-edit" slot-scope="{ model }">
                  <el-date-picker
                    v-model="model.expires_at"
                    type="datetime"
                    :placeholder="$t('ApiIntegration_ExpiresAt_Placeholder')"
                    :picker-options="expiryPickerOptions"
                    value-format="timestamp"
                    :clearable="true"
                  />
                </template>
              </rest-list-column>
              <rest-list-column-date
                prop="updated_at"
                :title="$t('ApiIntegration_UpdatedAt_Column_Title')"
                :filterable="true"
                :sortable="true"
                :editable="false"
                :width="180"
              />
              <rest-list-column
                prop="updated_by"
                :title="$t('ApiIntegration_UpdatedBy_Column_Title')"
                :filterable="false"
                :sortable="false"
                :editable="false"
              />

              <rest-list-column-actions :editable="true" :wrap="false">
                <template slot-scope="scope">
                  <rest-list-action-delete
                    :endpoint="rowDeleteEndpoint(scope.row)"
                    :name="scope.row.name"
                  />
                </template>
              </rest-list-column-actions>
            </rest-list-editable>
          </el-col>
        </el-row>
      </form-section>
      <form-section>
        <el-row>
          <el-col :span="12">
            <rest-form-item
              prop="endpoint"
              :label="$t('ApiIntegration_Field_Endpoint')"
            >
              <el-input v-model="apiEndpoint" disabled />
            </rest-form-item>
          </el-col>

          <el-col
            :span="12"
            v-if="formModel.apptoken_enabled && formModel.apptoken != null"
          >
            <rest-form-item
              prop="apptoken"
              :label="$t('ApiIntegration_Field_AppToken')"
            >
              <el-input
                v-model="appToken"
                disabled
                :class="{ blurred: !showToken }"
              />
              <icon
                v-if="!showToken"
                glyph="eye"
                size="20"
                @click="showToken = true"
                :alt="$t('ApiIntegration_Button_AppTokenReveal')"
                :title="$t('ApiIntegration_Button_AppTokenReveal')"
              />
              <icon
                v-else
                glyph="eye-crossed"
                size="20"
                @click="showToken = false"
                :alt="$t('ApiIntegration_Button_AppTokenHide')"
                :title="$t('ApiIntegration_Button_AppTokenHide')"
              />
            </rest-form-item>
          </el-col>
        </el-row>
      </form-section>
    </template>
  </rest-form-page>
</template>

<script>
import RestFormPage from '@/common/components/rest/RestFormPage'
import RestFormItem from '@/common/components/rest/RestFormItem'
import FormSection from '@/common/components/form/FormSection'
import RestListEditable from '@/common/components/rest/RestListEditable'
import { projectEndpoint, projectTimezoneTimestamp } from '@/core/helpers'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import RestListColumnDate from '@/common/components/rest/RestListColumnDate'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestApi from '@/core/services/RestApi'
import { oauthTypeEnum } from '@/modules/settings/enum/OauthTypeEnum'
import _ from 'lodash'

export default {
  name: 'ApiIntegrationPage',

  components: {
    RestListColumnDate,
    RestListColumnActions,
    RestListColumnEnum,
    RestListActionDelete,
    RestListColumn,
    RestListEditable,
    RestFormPage,
    RestFormItem,
    FormSection
  },

  data() {
    return {
      updateKey: 0,
      formModel: null,
      showToken: false,
      showMode: null,
      showData: null,
      updateKeyRow: null,
      addPublicKey: null,
      generatedSecret: null,
      oauthTypeEnum
    }
  },

  computed: {
    rules() {
      return {
        name: { required: true }
      }
    },

    fetchTokenEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `oauth-keys`,
        params: {
          sort: 'id',
          'per-page': 5
        }
      })
    },

    addTokenEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `oauth-keys`
      })
    },

    pageTitle() {
      return this.$t(this.$route.meta.title)
    },

    fetchEndpoint() {
      return {
        method: 'get',
        url: `projects/${this.$route.query.project_id}?expand=apptoken`,
        handler: data => {
          this.formModel = data
        }
      }
    },

    apiEndpoint() {
      return 'https://' + this.$store.state.app.domains.external_api
    },

    appToken: {
      get() {
        if (this.showToken) {
          return this.formModel.apptoken
        } else {
          return '•'.repeat(this.formModel.apptoken.length)
        }
      }
    },

    dialogTitle() {
      if (this.showMode === 'fingerprint') {
        return this.$t('ApiIntegration_PopupFp_Title')
      }
      if (this.showMode === 'publicKey') {
        return this.$t('ApiIntegration_PopupPk_Title')
      }
      return ''
    },

    expiryPickerOptions() {
      return {
        disabledDate(date) {
          return date.getTime() < Date.now() - 24 * 60 * 60 * 1000
        }
      }
    }
  },
  methods: {
    formatDate(timestamp) {
      if (!timestamp) return ''
      return projectTimezoneTimestamp(timestamp).format('DD MMM YYYY HH:mm:ss')
    },
    showKey({ row }) {
      this.showMode = 'publicKey'
      this.showData = row.public_key
    },
    showFp({ row }) {
      this.showMode = 'fingerprint'
      this.showData = row.public_key
    },
    addKey({ row }) {
      this.updateKeyRow = row
    },
    copyKey() {
      navigator.clipboard.writeText(this.showData).then(() => {
        this.showMode = null
        this.showData = null
        this.$message({
          message: this.$t('ApiIntegration_PublicKey_CopiedSuccessfully'),
          type: 'success'
        })
      })
    },
    closeKeyDialog() {
      this.showMode = null
      this.showData = null
      this.updateKeyRow = false
      this.addPublicKey = null
    },
    updatePublicKey() {
      let row = _.clone(this.updateKeyRow)
      row.public_key = this.addPublicKey
      RestApi(
        projectEndpoint({
          method: 'patch',
          url: `oauth-keys/${row.id}`,
          data: row
        })
      )
        .then(({ data }) => {
          this.updateKeyRow.public_key = this.addPublicKey
          this.closeKeyDialog()
          this.$message({
            message: this.$t('ApiIntegration_Success_Msg'),
            type: 'success'
          })
          this.$refs['rest-list'].$refs['restList'].refresh()
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },
    dataLoaded() {
      this.updateKey++
    },
    beforeSaveRow(row) {
      if (row.expires_at && row.expires_at > 9999999999) {
        row.expires_at = Math.floor(row.expires_at / 1000)
      }
    },
    addRow() {
      const newRow = {
        type: 2,
        newRow: true
      }
      this.$refs['rest-list'].addRow(newRow)
    },

    rowDeleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `oauth-keys/${row.id}`
      })
    },
    setScopes({ row }) {
      this.$router.push({
        name: 'OauthKeyScopes',
        query: {
          project_id: this.$route.query.project_id,
          key_id: row.id
        }
      })
    },
    onRowUpdated(row, index, data) {
      if (data.generated_secret) {
        this.generatedSecret = data.generated_secret
      }
    },
    copySecret() {
      navigator.clipboard.writeText(this.generatedSecret).then(() => {
        this.$message({
          message: this.$t('ApiIntegration_Secret_CopiedSuccessfully'),
          type: 'success'
        })
      })
    },
    closeSecretDialog() {
      this.generatedSecret = null
    }
  }
}
</script>

<style scoped>
.el-form-item__content .icon {
  position: absolute;
  padding: 10px;
  top: 0;
  right: 0;
}

.el-form-item__content .blurred {
  -webkit-filter: blur(2px);
  -moz-filter: blur(2px);
  -ms-filter: blur(2px);
  -o-filter: blur(2px);
  filter: blur(2px);
}

>>> .popup-textarea textarea {
  height: 300px;
  resize: none;
}

.copy-key-button {
  position: absolute;
  top: 80px;
  right: 40px;
  z-index: 1;

  font-size: 20px;
}

.copy-secret-button {
  position: absolute;
  top: 134px;
  right: 40px;
  z-index: 1;
  font-size: 20px;
}
</style>



// WEBPACK FOOTER //
// src/modules/settings/pages/ApiIntegrationPage.vue